/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import ReactHookSelect from 'react-hook-select';
import { ReactComponent as DropDownIcon } from 'Assets/svg/dropdown-icon.svg';
import { purchaseStatusPopupState } from 'Components/common/purchaseStatusPopup';
import InputWithButton from 'Components/common/inputWithButton';
import CancelSaveButton from 'Components/common/CancelSaveButton';
import userDetail from 'Stores/userDetail';
import profileState from 'Stores/profile';
import * as CONSTANTS from 'Utils/constants';
import Utils from 'Utils/utils';
import './profileSettings.scss';
import ChangePasswordPopup from './changePasswordPopup/changePasswordPopup';

const renderDropDownIcon = ({ isDropDownOpen }) => {
  return <DropDownIcon className={isDropDownOpen ? 'open' : ''} />;
};

function getTimeZoneValue(timeZone) {
  return `${
    timeZone.zone_value?.includes('(')
      ? timeZone.zone_value
      : `(${timeZone.zone_value})`
  } ${timeZone.zone_name}`;
}

function getPersonalInfo(userInfo) {
  const { firstName, lastName, email, timeZone } = userInfo;

  return {
    firstName,
    lastName: lastName || '',
    email,
    timeZone: timeZone.zone_value ? getTimeZoneValue(timeZone) : timeZone,
  };
}

const ProfileSettings = observer(() => {
  const [personalInfo, setPersonalInfo] = useState(
    getPersonalInfo(userDetail.userInfo),
  );
  const [showChangePasswordPopup, setShowChangePasswordPopup] = useState(false);

  const { userInfo } = userDetail;
  const { apiKey } = userInfo;

  const copyFunction = () => {
    Utils.copyToClipBoard(apiKey);
    purchaseStatusPopupState.setToastMsg(
      null,
      null,
      null,
      null,
      null,
      'Token Copied',
    );
  };

  useEffect(() => {
    setPersonalInfo(getPersonalInfo(userDetail.userInfo));
  }, [userDetail.userInfo]);

  return (
    <>
      <div className="title-with-card">
        <div className="personal-info-title">Personal Info</div>
        <div className="profile-card personal-info-card">
          <form className="name-section">
            <div className="image-placeholder">
              {userInfo.name
                ? userInfo.name.substring(0, 1)
                : userInfo.email
                ? userInfo.email.substring(0, 1)
                : ''}
            </div>
            <div className="first-name-wrapper">
              <label htmlFor="first-name">
                First Name
                <input
                  onChange={e => {
                    setPersonalInfo({
                      ...personalInfo,
                      firstName: e.target.value,
                    });
                  }}
                  required
                  id="first-name"
                  type="text"
                  value={personalInfo.firstName}
                />
              </label>
            </div>
            <div className="last-name-wrapper">
              <label htmlFor="last-name">
                Last Name
                <input
                  id="last-name"
                  type="text"
                  value={personalInfo.lastName}
                  onChange={e => {
                    setPersonalInfo({
                      ...personalInfo,
                      lastName: e.target.value,
                    });
                  }}
                />
              </label>
            </div>
            {(personalInfo.firstName !== userInfo.firstName ||
              personalInfo.lastName !== (userInfo.lastName || '')) && (
              <CancelSaveButton
                handleSave={() => {
                  if (personalInfo.firstName)
                    profileState.updateName({
                      firstName: personalInfo.firstName,
                      lastName: personalInfo.lastName,
                    });
                }}
                handleCancel={() => {
                  setPersonalInfo(
                    getPersonalInfo({
                      ...personalInfo,
                      firstName: userInfo.firstName,
                      lastName: userInfo.lastName,
                    }),
                  );
                }}
              />
            )}
          </form>
          <div className="time-zone-wrapper">
            <div>
              <div className="time-zone-label">Time Zone</div>
              <ReactHookSelect
                renderDropDownIcon={renderDropDownIcon}
                selectWrapperProps={{ className: 'timezone-dropdown' }}
                options={CONSTANTS.timezones.map(timezone => {
                  return {
                    label: timezone,
                    value: timezone,
                  };
                })}
                value={[personalInfo.timeZone]}
                getValue={value => {
                  setPersonalInfo({ ...personalInfo, timeZone: value[0] });
                }}
              />
            </div>
            {personalInfo.timeZone !== getTimeZoneValue(userInfo.timeZone) && (
              <CancelSaveButton
                handleSave={() =>
                  profileState.updateTimeZone(personalInfo.timeZone)
                }
                handleCancel={() => {
                  setPersonalInfo(
                    getPersonalInfo({
                      ...personalInfo,
                      timeZone: getTimeZoneValue(userInfo.timeZone),
                    }),
                  );
                }}
              />
            )}
          </div>
          <div className="registered-email-section">
            <div className="profile-sub-title">Registered Email ID</div>
            <div className="profile-message-text">
              Your registered email address is <span>{personalInfo.email}</span>
              . To change your email you can write to us at{' '}
              <a href="mailto:support@adapt.io">support@adapt.io</a>
            </div>
          </div>
          <div className="change-password-section">
            <div className="left-section">
              <div className="profile-sub-title">Change Password</div>
              <div className="profile-message-text">
                Your password must contain atleast 6 characters
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowChangePasswordPopup(true)}
            >
              Change Password
            </button>
            {showChangePasswordPopup && (
              <ChangePasswordPopup
                onClose={() => setShowChangePasswordPopup(false)}
              />
            )}
          </div>
        </div>
      </div>
      <div className="title-with-card">
        <div className="integrations-title">Integration</div>
        <div className="profile-card integrations-card">
          <div className="api-token">
            <div className="profile-sub-title">API Token</div>
            <InputWithButton
              className="api-token-wrapper"
              inputProps={{
                placeholder: 'API Token',
                disabled: true,
                value: apiKey,
              }}
              buttonProps={{
                id: 'copy',
                type: 'button',
                btnText: 'Copy',
                loader: false,
                click: copyFunction,
              }}
            />
          </div>
        </div>
      </div>
      <div className="title-with-card">
        <div className="account-title">Account</div>
        <div className="profile-card account-card">
          <div className="profile-sub-title">Deactivate my account</div>
          <div className="profile-message-text">
            By deactivating your account, you will no longer receive
            notifications from Adapt. Please make sure to save your information
            before proceeding to deactivate. To deactivate your account, please
            write to <a href="mailto:support@adapt.io">support@adapt.io</a>
          </div>
        </div>
      </div>
    </>
  );
});

export default ProfileSettings;
